// Vendor sign-in page. Standalone — no VendorLayout, no app chrome.
// Mirror of PortalLogin.tsx with the identity, redirect targets, and
// copy substituted. Public route (no guard); on success the
// onAuthStateChange handler in lib/auth.ts loads the vendor_users row
// and VendorProtectedRoute around /vendor/* takes over.
//
// Forgot-password flow lives inline (mode='forgot') and routes the
// recovery link to /vendor/set-password — symmetrical to the portal's
// /portal/set-password path. The set-password page handles the
// PASSWORD_RECOVERY event the same way it handles invite links.
//
// Anti-enumeration copy is identical regardless of whether the email
// exists — Supabase's resetPasswordForEmail does not leak existence
// either.
//
// Cross-role wrinkle (same shape as PortalLogin's): a staff member
// entering their email here gets a reset link → lands on
// /vendor/set-password → setting a password succeeds → page tries to
// navigate to /vendor → VendorProtectedRoute sees no vendor_users row
// and bounces them to /app. Net effect: staff password reset works;
// no vendor access leaks. Not guarding.

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Zap, Mail, Lock, Loader2, AlertCircle, ArrowLeft, CheckCircle2 } from 'lucide-react'
import { supabase } from '@/lib/supabase'

type Mode = 'signin' | 'forgot'

export default function VendorLogin() {
  const navigate = useNavigate()
  const [mode, setMode] = useState<Mode>('signin')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [resetSent, setResetSent] = useState(false)

  const onSignin = async (e: React.FormEvent) => {
    e.preventDefault()
    if (busy) return
    setBusy(true); setErr(null)
    const { error } = await supabase.auth.signInWithPassword({ email: email.trim(), password })
    if (error) {
      setBusy(false)
      setErr(error.message || 'Sign-in failed')
      return
    }
    navigate('/vendor', { replace: true })
  }

  const onForgot = async (e: React.FormEvent) => {
    e.preventDefault()
    if (busy) return
    setBusy(true); setErr(null)
    const redirectTo = `${window.location.origin}/vendor/set-password`
    const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), { redirectTo })
    setBusy(false)
    if (error) {
      console.warn('vendor reset error:', error.message)
    }
    setResetSent(true)
  }

  const backToSignin = () => {
    setMode('signin')
    setErr(null)
    setResetSent(false)
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--canvas)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, position: 'relative', overflow: 'hidden' }}>
      <div aria-hidden="true" style={{ position: 'absolute', inset: 0, pointerEvents: 'none', background: 'radial-gradient(circle at 20% 20%, var(--accent-glow), transparent 55%), radial-gradient(circle at 80% 80%, var(--accent-glow), transparent 55%)' }} />
      <div style={{ width: '100%', maxWidth: 380, position: 'relative' }}>
        <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, ease: 'easeOut' }}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginBottom: 28 }}>
          <div style={{ width: 38, height: 38, borderRadius: 10, background: 'var(--logo-bg-gradient)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Zap style={{ width: 18, height: 18, color: 'var(--accent-ink)' }} />
          </div>
          <p style={{ fontSize: 18, fontWeight: 800, color: 'var(--ink-primary)', margin: 0, letterSpacing: '-0.02em' }}>
            Vendor Portal
          </p>
        </motion.div>

        {mode === 'signin' && (
          <motion.form onSubmit={onSignin}
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, ease: 'easeOut', delay: 0.1 }}
            style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 16, padding: 24, display: 'flex', flexDirection: 'column', gap: 14, boxShadow: 'var(--shadow-popover-mid)' }}>
            <h1 style={{ fontSize: 18, fontWeight: 800, color: 'var(--ink-primary)', margin: '0 0 4px', letterSpacing: '-0.02em' }}>Sign in</h1>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Email</label>
              <div style={{ position: 'relative' }}>
                <Mail style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', width: 14, height: 14, color: 'var(--ink-tertiary)' }} />
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} required autoFocus
                  placeholder="you@example.com"
                  style={{ width: '100%', padding: '10px 12px 10px 33px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 8, fontSize: 14, color: 'var(--ink-primary)', outline: 'none', boxSizing: 'border-box' }} />
              </div>
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 5 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Password</label>
                <button type="button" onClick={() => { setMode('forgot'); setErr(null) }}
                  style={{ fontSize: 11, color: 'var(--accent-text)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontWeight: 600 }}>
                  Forgot password?
                </button>
              </div>
              <div style={{ position: 'relative' }}>
                <Lock style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', width: 14, height: 14, color: 'var(--ink-tertiary)' }} />
                <input type="password" value={password} onChange={e => setPassword(e.target.value)} required
                  placeholder="••••••••"
                  style={{ width: '100%', padding: '10px 12px 10px 33px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 8, fontSize: 14, color: 'var(--ink-primary)', outline: 'none', boxSizing: 'border-box' }} />
              </div>
            </div>
            {err && (
              <div role="alert" style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '8px 10px', background: 'var(--danger-dim)', border: '1px solid var(--danger-border)', borderRadius: 7 }}>
                <AlertCircle style={{ width: 13, height: 13, color: 'var(--danger)', flexShrink: 0, marginTop: 2 }} />
                <span style={{ fontSize: 12, color: 'var(--danger)' }}>{err}</span>
              </div>
            )}
            <button type="submit" disabled={busy}
              style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7, padding: '11px', background: 'var(--accent)', border: 'none', borderRadius: 9, color: 'var(--accent-ink)', fontSize: 14, fontWeight: 700, cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.7 : 1 }}>
              {busy && <Loader2 style={{ width: 14, height: 14, animation: 'spin 1s linear infinite' }} />}
              {busy ? 'Signing in…' : 'Sign in'}
            </button>
            <p style={{ fontSize: 11, color: 'var(--ink-tertiary)', margin: '4px 0 0', textAlign: 'center' }}>
              Need access? Contact your service provider.
            </p>
          </motion.form>
        )}

        {mode === 'forgot' && (
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 16, padding: 24, display: 'flex', flexDirection: 'column', gap: 14 }}>
            <button type="button" onClick={backToSignin}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, color: 'var(--ink-tertiary)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, alignSelf: 'flex-start' }}>
              <ArrowLeft style={{ width: 11, height: 11 }} /> Back to sign in
            </button>
            {!resetSent ? (
              <form onSubmit={onForgot} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <h1 style={{ fontSize: 18, fontWeight: 800, color: 'var(--ink-primary)', margin: '0 0 4px', letterSpacing: '-0.02em' }}>Reset password</h1>
                <p style={{ fontSize: 12, color: 'var(--ink-secondary)', margin: 0 }}>
                  Enter the email address you use for the vendor portal. If it's recognized, we'll send a link to set a new password.
                </p>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Email</label>
                  <div style={{ position: 'relative' }}>
                    <Mail style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', width: 14, height: 14, color: 'var(--ink-tertiary)' }} />
                    <input type="email" value={email} onChange={e => setEmail(e.target.value)} required autoFocus
                      placeholder="you@example.com"
                      style={{ width: '100%', padding: '10px 12px 10px 33px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 8, fontSize: 14, color: 'var(--ink-primary)', outline: 'none', boxSizing: 'border-box' }} />
                  </div>
                </div>
                <button type="submit" disabled={busy}
                  style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7, padding: '11px', background: 'var(--accent)', border: 'none', borderRadius: 9, color: 'var(--accent-ink)', fontSize: 14, fontWeight: 700, cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.7 : 1 }}>
                  {busy && <Loader2 style={{ width: 14, height: 14, animation: 'spin 1s linear infinite' }} />}
                  {busy ? 'Sending…' : 'Send reset link'}
                </button>
              </form>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <h1 style={{ fontSize: 18, fontWeight: 800, color: 'var(--ink-primary)', margin: '0 0 4px', letterSpacing: '-0.02em' }}>Check your email</h1>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '10px 12px', background: 'var(--positive-dim)', border: '1px solid var(--positive-border)', borderRadius: 8 }}>
                  <CheckCircle2 style={{ width: 14, height: 14, color: 'var(--positive)', flexShrink: 0, marginTop: 2 }} />
                  <p style={{ fontSize: 12, color: 'var(--ink-primary)', margin: 0, lineHeight: 1.5 }}>
                    If that email has vendor portal access, a reset link is on its way. Check your inbox in the next few minutes.
                  </p>
                </div>
                <p style={{ fontSize: 11, color: 'var(--ink-tertiary)', margin: 0, textAlign: 'center' }}>
                  Didn't get it? Check spam, or contact your service provider.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
